import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import DashboardLayout from '../components/DashboardLayout';
import { api, endpoints } from '../api/config';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import { COLUMN_MIN_SIZE, COLUMN_SIZE } from '../constants/styles';

const toTitle = (key) =>
  String(key)
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getLabelErrorMessage = (err) => {
  const status = err?.response?.status;
  const apiMessage = String(err?.response?.data?.message || err?.response?.data?.detail || '').toLowerCase();
  if (status === 404 || apiMessage.includes('not picked') || apiMessage.includes('item is not picked')) {
    return 'Item is not picked';
  }
  return err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Failed to generate label';
};

const DropshipSalesOrderDetails = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const [searchParams] = useSearchParams();
  const decodedTransactionId = decodeURIComponent(transactionId || '').trim();
  const viewMode = searchParams.get('view') === 'nested' ? 'nested' : 'outer';

  const [lineLevelData, setLineLevelData] = useState([]);
  const [detailedData, setDetailedData] = useState([]);
  const [lineLevelLoading, setLineLevelLoading] = useState(true);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [error, setError] = useState(null);
  const [labelImageOpen, setLabelImageOpen] = useState(false);
  const [labelImages, setLabelImages] = useState([]);
  const [labelLoading, setLabelLoading] = useState(false);
  const [labelError, setLabelError] = useState(null);

  const fetchLineLevel = useCallback(async () => {
    if (!decodedTransactionId) {
      setLineLevelData([]);
      setError('Missing transaction id');
      setLineLevelLoading(false);
      return;
    }

    setLineLevelLoading(true);
    setError(null);
    try {
      const response = await api.get(endpoints.dropshipSoLineLevel, {
        params: { transaction_id: decodedTransactionId },
      });
      const responseData = response.data?.data || response.data;
      const list = Array.isArray(responseData) ? responseData : responseData ? [responseData] : [];
      setLineLevelData(list);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to load dropship sales order details');
      setLineLevelData([]);
    } finally {
      setLineLevelLoading(false);
    }
  }, [decodedTransactionId]);

  useEffect(() => {
    fetchLineLevel();
  }, [fetchLineLevel]);

  const lineLevelColumns = useMemo(() => {
    if (!lineLevelData.length) return [];
    const keys = Object.keys(lineLevelData[0] || {}).filter((k) => k !== 'data');
    return keys.map((key) => ({
      accessorKey: key,
      header: toTitle(key),
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined || value === '') return '-';
        return String(value);
      },
    }));
  }, [lineLevelData]);

  const nestedData = useMemo(
    () => lineLevelData.flatMap((row) => (Array.isArray(row?.data) ? row.data : [])),
    [lineLevelData]
  );

  const extractSsccCodesFromRow = useCallback((row) => {
    if (!row || typeof row !== 'object') return [];
    const key = Object.keys(row).find((k) => {
      const normalized = k.toLowerCase();
      return normalized === 'sscc_codes' || normalized === 'sscc_code' || normalized === 'sscc';
    });
    if (!key || row[key] === null || row[key] === undefined) return [];
    return Array.from(
      new Set(
        String(row[key])
          .split(/[;,\n]/)
          .map((item) => item.trim())
          .filter(Boolean)
      )
    );
  }, []);

  const mapJsonToZpl = useCallback((raw, fallbackSsccCode = '') => {
    const payload = raw?.label_data ? raw.label_data : raw || {};
    const from = payload.shipping_from || {};
    const to = payload.shipping_to || {};
    const customerName = String(payload.customer_name || payload.customer || '').trim();
    const isAmazonCustomer = customerName.toUpperCase() === 'AMAZON-1YZ7N';
    const nameFrom = String(from.name1 || from.name || from.company || '').trim();
    const secondaryFrom = String(from.name2 || '').trim();
    const addressFrom = String(from.address || '');
    const cityFrom = String(from.city || '');
    const stateFrom = String(from.state || '');
    const zipFrom = String(from.zip || '');
    const companyTo = String(to.company || '');
    const addressTo = String(to.address || '');
    const cityTo = String(to.city || '');
    const stateTo = String(to.state || '');
    const zipTo = String(to.zip || '');
    const company = companyTo.split(',')[0].trim();
    const adress = companyTo.includes(',') ? companyTo.split(',').slice(1).join(',').trim() : '';
    let adress1 = '';
    let adress2 = '';
    if (addressTo.includes('&')) {
      const parts = addressTo.split('&');
      adress1 = parts[0].trim();
      if (parts.length > 1) adress2 = parts[1].trim();
    } else {
      adress1 = addressTo;
    }
    const poNumber = String(payload.po_number || '');
    const model = String(payload.model || '');
    const upc = String(payload.upc || '');
    const cartonInfo = String(payload.carton_info || '');
    const bolNumberDisplay = String(payload.bol_number || '').replaceAll('-', '');
    const ssccCode = String(payload.sscc_code || raw?.sscc_code || fallbackSsccCode || '');
    const ssccDisplay = String(payload.sscc_display || ssccCode);
    const zipCode = String(payload.zip_code || '');
    const zipDisplay = String(payload.zip_display || zipCode);
    const proNumber = String(payload.pro_number || '');
    const carrierName = String(payload.carrier_name || payload.carrier || '').trim();
    const qtyDisplay = '1';
    const addressBlock =
      adress2.trim().length > 0
        ? `^FO420,165^FD${adress1}^FS\n^FO420,195^FD${adress2}^FS\n^FO420,225^FD${cityTo}, ${stateTo} ${zipTo}^FS`
        : `^FO420,165^FD${adress1}^FS\n^FO420,195^FD${cityTo}, ${stateTo} ${zipTo}^FS`;
    const carrierLabel = isAmazonCustomer ? carrierName : 'CARRIER';
    const isLongCarrier = carrierLabel.trim().length > 22;
    const carrierZpl = isLongCarrier
      ? `^CF0,28\n^FO420,315^FB280,2,4,L,0^FD${carrierLabel}^FS`
      : `^CF0,28\n^FO420,315^FD${carrierLabel}^FS`;
    const proBolFontCmd = isLongCarrier ? '^CF0,22' : '^CF0,24';
    const proY = isLongCarrier ? '385' : '360';
    const bolY = isLongCarrier ? '415' : '390';
    const bolLine = isAmazonCustomer ? `^FO420,${bolY}^FDBOL#: ${bolNumberDisplay}^FS` : `^FO420,${bolY}^FDBOL#:^FS`;
    const upcLine = isAmazonCustomer ? `^FO20,635^FDUPC: ${upc} / SINGLE ASIN^FS` : `^FO20,635^FDUPC: ${upc}^FS`;
    const template = `^XA
^PW772
^LL1178
^CI28
^CF0,38
^FO20,30^FDSHIP FROM:^FS
^FO420,30^FDSHIP TO:^FS
^CF0,28
^FO20,100^FD$nameFrom^FS
^FO20,135^FD$secondaryFrom^FS
^FO20,170^FD$addressFrom^FS
^FO20,205^FD$cityFrom, $stateFrom, $zipFrom^FS
^FO420,95^FD$company^FS
^FO420,130^FD$adress^FS
$addressBlock
^FO390,20^GB2,440,7^FS
^FO15,265^GB740,7,7^FS
^BY2,2,95
^FO55,315^BCN,95,N,N,N^FD$zipCode^FS
^CF0,26
^FO30,420^FB320,1,0,C,0^FD$zipDisplay^FS
$carrierZpl
$proBolFontCmd
^FO420,$proY^FDPro#: $proNumber^FS
$bolLine
^FO15,460^GB740,7,7^FS
^CF0,28
^FO20,490^FDPO# $poNumber^FS
^BY2,2,85
^FO420,510^BCN,85,N,N,N^FD$poNumber^FS
^CF0,28
$upcLine
^FO20,675^FDQTY: $qtyDisplay^FS
^CF0,28
^FO20,715^FDCARTON:^FS
^FO140,715^FD$cartonInfo^FS
^CF0,28
^FO20,755^FDMODEL: $model^FS
^FO15,795^GB740,7,7^FS
^CF0,30
^FO8,825^FB772,1,0,C,0^FDSSCC - 18^FS
^BY2.5,3,200
^FO2.5,885^BCN,200,N,N,N^FD$ssccCode^FS
^CF0,38
^FO0,1120^FB772,1,0,C,0^FD$ssccDisplay^FS
^XZ`;
    return template
      .replaceAll('$nameFrom', nameFrom)
      .replaceAll('$secondaryFrom', secondaryFrom)
      .replaceAll('$addressFrom', addressFrom)
      .replaceAll('$cityFrom', cityFrom)
      .replaceAll('$stateFrom', stateFrom)
      .replaceAll('$zipFrom', zipFrom)
      .replaceAll('$company', company)
      .replaceAll('$adress', adress)
      .replaceAll('$addressBlock', addressBlock)
      .replaceAll('$zipCode', zipCode)
      .replaceAll('$zipDisplay', zipDisplay)
      .replaceAll('$carrierZpl', carrierZpl)
      .replaceAll('$proBolFontCmd', proBolFontCmd)
      .replaceAll('$proY', String(proY))
      .replaceAll('$proNumber', proNumber)
      .replaceAll('$bolLine', bolLine)
      .replaceAll('$poNumber', poNumber)
      .replaceAll('$upcLine', upcLine)
      .replaceAll('$qtyDisplay', qtyDisplay)
      .replaceAll('$cartonInfo', cartonInfo)
      .replaceAll('$model', model)
      .replaceAll('$ssccCode', ssccCode)
      .replaceAll('$ssccDisplay', ssccDisplay);
  }, []);

  const convertZplToImage = useCallback(async (zpl) => {
    const response = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/', {
      method: 'POST',
      headers: {
        Accept: 'image/png',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: zpl,
    });
    if (!response.ok) throw new Error('Failed to render label image');
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }, []);

  const clearLabelImages = useCallback(() => {
    setLabelImages((prev) => {
      prev.forEach((item) => {
        if (item?.imageUrl) URL.revokeObjectURL(item.imageUrl);
      });
      return [];
    });
  }, []);

  const handleGenerateLabel = useCallback(
    async (row) => {
      const ssccCodes = extractSsccCodesFromRow(row);
      if (ssccCodes.length === 0) {
        setLabelError('No SSCC code found for this row');
        setLabelImageOpen(true);
        return;
      }

      setLabelLoading(true);
      setLabelError(null);
      clearLabelImages();
      setLabelImageOpen(true);
      try {
        const items = [];
        for (const ssccCode of ssccCodes) {
          const response = await api.get(`/item-reprint-label?sscc_code=${encodeURIComponent(ssccCode)}`);
          const zpl = mapJsonToZpl(response.data, ssccCode);
          const imageUrl = await convertZplToImage(zpl);
          items.push({ ssccCode, imageUrl });
        }
        setLabelImages(items);
      } catch (err) {
        setLabelError(getLabelErrorMessage(err));
      } finally {
        setLabelLoading(false);
      }
    },
    [clearLabelImages, convertZplToImage, extractSsccCodesFromRow, mapJsonToZpl]
  );

  const addGenerateLabelColumn = useCallback(
    (columns) => [
      {
        accessorKey: '__generate_label',
        header: 'Generate Label',
        forceFirst: true,
        minSize: 120,
        size: 140,
        cell: (info) => {
          const row = info.row?.original || info.getValue();
          const hasSscc = extractSsccCodesFromRow(row).length > 0;
          if (!hasSscc) return '-';
          return (
            <Button size="small" variant="outlined" onClick={() => handleGenerateLabel(row)} disabled={labelLoading}>
              Generate Label
            </Button>
          );
        },
      },
      ...columns,
    ],
    [extractSsccCodesFromRow, handleGenerateLabel, labelLoading]
  );

  const nestedColumns = useMemo(() => {
    if (!nestedData.length) return [];
    const keys = Object.keys(nestedData[0] || {}).filter((k) => k !== 'data');
    return keys.map((key) => ({
      accessorKey: key,
      header: toTitle(key),
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined || value === '') return '-';
        return String(value);
      },
    }));
  }, [nestedData]);

  const detailedColumns = useMemo(() => {
    if (!detailedData.length) return [];
    const keys = Object.keys(detailedData[0] || {}).filter((k) => k !== 'data');
    return keys.map((key) => ({
      accessorKey: key,
      header: toTitle(key),
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined || value === '') return '-';
        return String(value);
      },
    }));
  }, [detailedData]);

  const getRowId = (row, index) => {
    const tx = String(row?.transaction_id || decodedTransactionId || '');
    const line = String(row?.line_id || row?.internal_id || row?.id || '');
    const item = String(row?.item || row?.name || '');
    return ['row', tx, line, item, index !== undefined ? String(index) : ''].filter(Boolean).join('_');
  };

  const primaryGridData = viewMode === 'nested' ? nestedData : lineLevelData;
  const primaryGridColumns = useMemo(
    () => addGenerateLabelColumn(viewMode === 'nested' ? nestedColumns : lineLevelColumns),
    [addGenerateLabelColumn, viewMode, nestedColumns, lineLevelColumns]
  );
  const primaryGridTitle =
    viewMode === 'nested'
      ? `Dropship ${decodedTransactionId} - Nested Response`
      : `Dropship ${decodedTransactionId} - Outer Response`;

  const primaryGrid = useDataGridProTable({
    data: primaryGridData,
    columns: primaryGridColumns,
    loading: lineLevelLoading,
    error: error || null,
    title: primaryGridTitle,
    onDataRefresh: fetchLineLevel,
    getRowId,
    onRowClick:
      viewMode === 'outer'
        ? (row) => {
            if (!Array.isArray(row?.data)) {
              setDetailedData([]);
              return;
            }
            setDetailedLoading(true);
            setDetailedData(row.data);
            setDetailedLoading(false);
          }
        : undefined,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
  });

  const detailedGrid = useDataGridProTable({
    data: detailedData,
    columns: useMemo(() => addGenerateLabelColumn(detailedColumns), [addGenerateLabelColumn, detailedColumns]),
    loading: detailedLoading,
    title: 'Detailed Rows',
    getRowId,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
  });

  return (
    <DashboardLayout>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate('/dropship-sales-orders')}>
            Back to Dropship Sales Orders
          </Button>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Dropship Order: {decodedTransactionId || 'N/A'} ({viewMode === 'nested' ? 'Nested' : 'Outer'} View)
          </Typography>
        </Box>

        <MuiXDataGridSurface hook={primaryGrid} DataGridPro={DataGridPro} />

        {viewMode === 'nested' ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
            Showing nested rows from the API response.
          </Typography>
        ) : detailedLoading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : detailedData.length > 0 ? (
          <MuiXDataGridSurface hook={detailedGrid} DataGridPro={DataGridPro} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
            Click a summary row to view nested detailed data.
          </Typography>
        )}
      </Box>
      <Dialog
        open={labelImageOpen}
        onClose={() => {
          setLabelImageOpen(false);
          clearLabelImages();
          setLabelError(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Label Preview
          <IconButton
            aria-label="close"
            onClick={() => {
              setLabelImageOpen(false);
              clearLabelImages();
              setLabelError(null);
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {labelError ? <Alert severity="error" sx={{ mb: 2 }}>{labelError}</Alert> : null}
          {labelLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {labelImages.map((item) => (
                <Box key={item.ssccCode} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2">SSCC: {item.ssccCode}</Typography>
                  <img src={item.imageUrl} alt={`Label ${item.ssccCode}`} style={{ maxWidth: '100%', border: '1px solid #ddd' }} />
                </Box>
              ))}
              {!labelImages.length && !labelError ? (
                <Typography variant="body2" color="text.secondary">
                  No label preview available.
                </Typography>
              ) : null}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default DropshipSalesOrderDetails;
